import React, { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from './Icon';
import { PHOTO_CONTROL_CLASSES } from './photoControls';

const VARIANT_CLASSES = {
  // Sheet and modal headers: a soft grey circle inside the tap area, the way
  // iOS sheets draw their close
  default: 'text-content-secondary hover:text-content-primary before:absolute before:inset-1.5 before:rounded-full before:bg-surface-secondary hover:before:bg-surface-tertiary',
  // One-line banners, hints, inline panels: the icon alone
  plain: 'text-content-secondary hover:text-content-primary hover:bg-surface-secondary',
  // Over a photo (same circle as the back/like/save/share controls)
  photo: PHOTO_CONTROL_CLASSES,
  // Lightboxes and other full-black viewers
  onDark: 'text-white/85 hover:text-white hover:bg-white/10',
};

/**
 * CloseButton — the one ✕ (Kurtis, 2026-10-02). Always a 44px tap area, the
 * same `close` icon, top right of whatever it closes. Replaces 28px modal
 * closes, 16–24px banner dismissals and "×" text glyphs.
 *
 * Props:
 * @param {Function} onClick
 * @param {'default'|'plain'|'photo'|'onDark'} variant
 * @param {'md'|'sm'} size - icon size only; the tap area stays 44px. sm for
 *   one-line banners and compact panels.
 * @param {string} ariaLabel - Defaults to "Close". Pass a specific one where
 *   it helps ("Dismiss notification", "Close photo preview").
 * @param {string} className - Placement (margins, absolute positioning)
 * Anything else (data-*, title, aria-*) passes through; a ref reaches the
 * <button> (focus-on-open in dialogs).
 */
const CloseButton = forwardRef(function CloseButton({ onClick, variant = 'default', size = 'md', ariaLabel = null, className = '', ...rest }, ref) {
  const { t } = useTranslation('common');
  // `relative` anchors the default variant's ::before circle, but Tailwind
  // emits `relative` after `absolute`/`fixed`, so it would silently win over a
  // caller's own positioning. Those already anchor ::before, so skip it then.
  const positioned = /(^|\s)!?(absolute|fixed|sticky)(\s|$)/.test(className);
  const classes = [
    positioned ? '' : 'relative',
    'w-11 h-11 flex-shrink-0 inline-flex items-center justify-center rounded-full transition-colors duration-fast',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-light',
    VARIANT_CLASSES[variant] || VARIANT_CLASSES.default,
    className,
  ].filter(Boolean).join(' ');
  return (
    <button ref={ref} type="button" onClick={onClick} className={classes} aria-label={ariaLabel || t('controls.close')} {...rest}>
      {/* relative: sits above the default variant's ::before circle */}
      <Icon name="close" className={`relative ${size === 'sm' ? 'w-4 h-4' : 'w-5 h-5'}`} />
    </button>
  );
});

export default CloseButton;
